/**
 * Fetch typology configs from the backend once per session and resolve a
 * single typology by key. Falls back to null while loading or when the
 * typology isn't in the DB — callers should keep their bundled JS as a fallback:
 *
 *     const dbTypology = useTypology('structuring')
 *     const bank = dbTypology?.scored_bank || BANK_FOR_TYPOLOGY['structuring']
 *
 * Icons inside section objects arrive from the API as lucide-react component
 * names (strings). They are resolved to actual components here so consumers
 * can render `<section.icon />` without caring whether the data came from the
 * DB or the bundled JS.
 */
import { useEffect, useState } from 'react'
import { authFetch, getLocalApiUrl } from '../config/api'
import {
  DollarSign, Eye, User, Clock, Shield, Users, CreditCard,
  CheckCircle2, FileText, AlertTriangle,
} from 'lucide-react'

const ICON_MAP = {
  DollarSign, Eye, User, Clock, Shield, Users, CreditCard,
  CheckCircle2, FileText, AlertTriangle,
}

let cachePromise = null

function hydrate(typology) {
  if (!typology) return typology
  // scored_bank section icons
  const bank = typology.scored_bank
  if (bank && Array.isArray(bank.sections)) {
    bank.sections = bank.sections.map((s) => ({
      ...s,
      icon: typeof s.icon === 'string' ? (ICON_MAP[s.icon] || s.icon) : s.icon,
    }))
  }
  // tree terminal node icons
  const tree = typology.tree
  if (tree && typeof tree === 'object') {
    for (const node of Object.values(tree)) {
      if (node && typeof node.icon === 'string') {
        // Promote to capital Icon for strategy components that destructure { Icon }.
        node.Icon = ICON_MAP[node.icon] || null
      }
    }
  }
  return typology
}

async function fetchAll() {
  try {
    const r = await authFetch(getLocalApiUrl('/api/typologies'))
    const j = await r.json()
    if (j?.success && Array.isArray(j.data)) {
      const byKey = {}
      for (const t of j.data) byKey[t.typology_key] = hydrate(t)
      return byKey
    }
  } catch (_) {
    /* fall through to empty map; consumers use their JS fallback */
  }
  return {}
}

export function useTypology(key) {
  const [typology, setTypology] = useState(null)
  useEffect(() => {
    if (!cachePromise) cachePromise = fetchAll()
    let cancelled = false
    cachePromise.then((map) => {
      if (!cancelled) setTypology(map[key] ?? null)
    })
    return () => { cancelled = true }
  }, [key])
  return typology
}
